import type {
	Product,
	ProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type {
	BasketItem,
	Basket
} from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { Order } from "@nwt/norce-pwa/dist/runtime/types/api/checkout/orders/orders";
import {
	createGtmItem,
	type CreateGtmItemContext
} from "../types/composables/useGtmEventsTypes";
import type { CombinedItem } from "~/types/components/category";

const sha256 = async (value: string): Promise<string> => {
	const buffer = await crypto.subtle.digest(
		"SHA-256",
		new TextEncoder().encode(value)
	);
	return Array.from(new Uint8Array(buffer))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
};

export const useGtmEvents = () => {
	const getLayer = () => {
		if (import.meta.client) {
			return ((window as unknown as { dataLayer?: unknown[] }).dataLayer =
				(window as unknown as { dataLayer?: unknown[] }).dataLayer || []);
		}
		return [];
	};
	const gtm = getLayer();
	const norceStore = useNorceStore();
	const currencyCode = norceStore?.currentCurrency?.Code || "SEK";

	if (!gtm) {
		return {
			customPageView: () => {},
			productImpression: () => {},
			productClick: () => {},
			productDetail: () => {},
			addToCart: () => {},
			addPaymentInfo: () => {},
			addShippingInfo: () => {},
			purchase: async () => {},
			removeFromCart: () => {},
			beginCheckout: () => {},
			viewCart: () => {},
			addToWishlist: () => {},
			userIdentify: async () => {},
			userAnonymize: () => {},
			cartState: () => {},
			cartCleared: () => {}
		};
	}

	// [NF-460] Same as base: Dotdigital's listing event rides on view_item_list.
	const dotdigital = useDotdigital();

	// Market as an ISO-3166 country code (e.g. "SE"), matching order.country.
	// Resolution order:
	//  1. checkout delivery-country override (set via syncCountryPriceList ->
	//     setCountryCode). It is recorded per locale, so another store's is not
	//     what this one reads and a locale switch needs no cleanup.
	//  2. the loaded application's default country (precise, once initApp ran).
	//  3. the region part of the locale iso (e.g. "sv-SE" -> "SE"), available
	//     synchronously from the SSR-rendered <html lang> so the very first
	//     page_view after a locale switch already reports the new market.
	const siteMarket = () =>
		norceStore?.countryCode ||
		norceStore?.applicationNorce?.Countries?.Default?.Code ||
		(import.meta.client
			? document.documentElement.lang?.split("-")[1]?.toUpperCase()
			: undefined) ||
		undefined;

	// [BB-171] Norce ApplicationId, emitted alongside site_market so GTM can
	// forward the application dimension to Klaviyo (location was already
	// covered by site_market; the application id was missing everywhere).
	const siteApplication = () => norceStore?.applicationNorce?.Id;

	const config = useRuntimeConfig();
	const cdnBaseUrl = (config.public.norce as { cdn?: string } | undefined)?.cdn;
	const itemContext: CreateGtmItemContext = {
		cdnBaseUrl,
		origin: import.meta.client ? window.location.origin : undefined
	};
	const mapItem = (
		item: Parameters<typeof createGtmItem>[0],
		category?: string | number
	) => createGtmItem(item, category, 1, itemContext);

	return {
		customPageView() {
			gtm.push({
				event: "custom_page_view",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication()
			});
		},

		productImpression(
			products: Partial<FilteredProductItem>[] | CombinedItem[],
			categoryName?: string
		) {
			const productItems = products
				.map((item) => {
					if (
						"type" in item &&
						item.type === "product" &&
						"data" in item &&
						item.data
					) {
						return item.data as Partial<FilteredProductItem>;
					}
					return item as Partial<FilteredProductItem>;
				})
				.filter((item) => item != null);

			gtm.push({
				event: "view_item_list",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					currency: currencyCode,
					items: productItems
						.map((product) => mapItem(product, categoryName))
						.filter((item) => item != null)
				}
			});
			dotdigital.productList(products);
		},

		productClick(product: Partial<FilteredProductItem> | ProductItem) {
			gtm.push({
				event: "select_item",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					currency: currencyCode,
					items: [mapItem(product)]
				}
			});
		},

		productDetail(product: Product) {
			gtm.push({
				event: "view_item",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					currency: currencyCode,
					value: product.Price || 0,
					items: [mapItem(product)]
				}
			});
		},

		beginCheckout(basket: Basket) {
			if (!basket?.Items?.length || !basket?.Summary) {
				console.warn("Basket items or summary are missing for purchase event");
				return;
			}

			gtm.push({
				event: "begin_checkout",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					coupon: basket.DiscountCode || "",
					value: basket.Summary.Total.AmountIncVat,
					currency: basket.CurrencyCode || currencyCode,
					items: basket.Items?.map((item) => mapItem(item)).filter(
						(item) => item != null
					)
				}
			});
		},

		addPaymentInfo(basket: Basket, paymentMethod?: string) {
			gtm.push({
				event: "add_payment_info",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					value: basket.Summary?.Total.AmountIncVat,
					currency: currencyCode,
					payment_type: paymentMethod || basket.PaymentMethodId?.toString(),
					items: basket.Items?.map((item) => mapItem(item))
				}
			});
		},

		addShippingInfo(basket: Basket, shippingMethod?: string) {
			gtm.push({
				event: "add_shipping_info",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					value: basket.Summary?.Total.AmountIncVat,
					currency: currencyCode,
					shipping_tier: shippingMethod || basket.DeliveryMethodId?.toString(),
					items: basket.Items?.map((item) => mapItem(item))
				}
			});
		},

		async purchase(basket: Basket, order?: Order, newCustomer?: boolean) {
			if (!basket.Items || !basket.Summary) {
				console.warn("Basket items or summary are missing for purchase event");
				return;
			}

			const billing = order?.customer?.billing;
			const email = billing?.email?.toLowerCase().trim();
			const phone = billing?.phone?.trim();

			const [sha256Email, sha256Phone] = await Promise.all([
				email ? sha256(email) : Promise.resolve(undefined),
				phone ? sha256(phone) : Promise.resolve(undefined)
			]);

			gtm.push({
				event: "purchase",
				event_id: crypto.randomUUID(),
				site_market: order?.country ?? siteMarket(),
				site_application: siteApplication(),
				user_data: billing
					? {
							...(email && { email_address: email }),
							...(sha256Email && { sha256_email_address: sha256Email }),
							...(phone && { phone_number: phone }),
							...(sha256Phone && { sha256_phone_number: sha256Phone }),
							address: {
								city: billing.city,
								postal_code: billing.postalCode,
								country: billing.country
							}
						}
					: undefined,
				ecommerce: {
					transaction_id: basket.OrderReference,
					value: basket.Summary.Items?.Amount ?? 0,
					currency: basket.CurrencyCode || currencyCode,
					tax: basket.Summary.Total?.Vat,
					shipping: basket.Summary.Freigt?.Amount,
					shipping_tier: order?.shippings?.[0]?.name,
					payment_type: order?.payments?.[0]?.name,
					coupon: basket.DiscountCode || "",
					new_customer: newCustomer ?? "not specified",
					items: basket.Items?.map((item) => mapItem(item)).filter(
						(item) => item != null
					)
				}
			});
		},

		addToCart(item: ProductItem | Product | Partial<BasketItem>) {
			gtm.push({
				event: "add_to_cart",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					currency: currencyCode,
					items: [mapItem(item)]
				}
			});
		},

		removeFromCart(item: Partial<BasketItem>) {
			gtm.push({
				event: "remove_from_cart",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					currency: currencyCode,
					items: [mapItem(item)]
				}
			});
		},

		viewCart(items: BasketItem[], orderTotal: number) {
			gtm.push({
				event: "view_cart",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					currency: currencyCode,
					value: orderTotal,
					items: items
						.filter((item) => item.Name !== "Frakt")
						.map((item) => mapItem(item))
				}
			});
		},

		addToWishlist(item: Product | Partial<FilteredProductItem> | ProductItem) {
			gtm.push({
				event: "add_to_wishlist",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					currency: currencyCode,
					items: [mapItem(item)]
				}
			});
		},

		shippingMethodAdded(shippingMethod: object) {
			gtm.push({
				event: "shipping_method_added",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					price: shippingMethod?.Price || 0,
					name: shippingMethod?.Name || "",
					carrier: shippingMethod?.Carrier || ""
				}
			});
		},

		// User identify: pushed when an authenticated user becomes known.
		// GTM tags route this to the configured CDP (Dotdigital, etc.).
		// Mirrors the purchase user_data pattern by emitting both plain and
		// sha256-hashed email so downstream tags can choose either.
		async userIdentify(email: string) {
			if (!email) return;
			const normalized = email.trim().toLowerCase();
			const sha256Email = await sha256(normalized);
			gtm.push({
				event: "user_identify",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				user: {
					email_address: normalized,
					sha256_email_address: sha256Email
				}
			});
		},

		// Logout / session anonymize.
		userAnonymize() {
			gtm.push({
				event: "user_anonymize",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication()
			});
		},

		// Stateful snapshot of the current cart. Fires on every basket mutation
		// so CDPs that track abandoned carts (Dotdigital cartInsight, Klaviyo
		// Started Cart, etc.) get a full picture rather than incremental deltas.
		cartState(basket: Basket) {
			if (!basket?.Items?.length || !basket.Summary) return;
			const cartUrl = import.meta.client
				? `${window.location.origin}/checkout`
				: undefined;
			gtm.push({
				event: "cart_state",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: {
					cart_id: basket.Id != null ? String(basket.Id) : undefined,
					currency: basket.CurrencyCode || currencyCode,
					subtotal: basket.Summary.Items?.Amount ?? 0,
					grand_total: basket.Summary.Total?.AmountIncVat ?? 0,
					tax_amount: basket.Summary.Total?.Vat,
					shipping: basket.Summary.Freigt?.AmountIncVat,
					discount_code: basket.DiscountCode || undefined,
					cart_url: cartUrl,
					items: basket.Items.map((item) => mapItem(item)).filter(
						(item) => item != null
					)
				}
			});
		},

		// Cart emptied — pair event to cart_state so CDPs can reset their
		// in-flight cart state.
		cartCleared() {
			gtm.push({
				event: "cart_cleared",
				event_id: crypto.randomUUID(),
				site_market: siteMarket(),
				site_application: siteApplication(),
				ecommerce: { currency: currencyCode, items: [] }
			});
		}
	};
};
